import { useToastProvider } from '@qualio/ui-components';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { documentApi } from '../../../../../../../api/document';
import {
  DocumentStatus,
  QualioDocument,
} from '../../../../../../../api/model/document';
import { useTags } from '../../../../../../../hooks/useTags';
import { useUsers } from '../../../../../../../hooks/useUsers';
import { ChangeDocumentOwnerModal } from '../../../../../ChangeDocumentOwnerModal/ChangeDocumentOwnerModal';
import {
  canUserAccessAtLeastOnePrivateTag,
  getDocumentTagIdsToGroupIdsMap,
} from '../../../../../DocumentOverview/DocumentProperties/utils';
import { useTableDataInvalidation } from '../../../hooks/useTableDataInvalidation';
import { WorkspaceTabs } from '../../../WorkspaceV2';
import {
  hasAllowedStatusesForAction,
  hasRequiredPermissionsForAction,
  isValidTabForAction,
} from '../../BulkActions';
import { BulkActionButton } from '../BulkActionButton';
import { ChangeOwnerButtonProps, IsActionAllowedCallback } from './types';

const canChangeOwner: IsActionAllowedCallback = (
  user,
  statuses,
  activeTab,
  requiredPermissions,
  selectedDocuments,
) => {
  if (
    !selectedDocuments.length ||
    !hasRequiredPermissionsForAction(requiredPermissions, user.permissions) ||
    !isValidTabForAction([WorkspaceTabs.ALL_DOCUMENTS], activeTab) ||
    !statuses.length
  ) {
    return false;
  }
  return hasAllowedStatusesForAction(
    [
      DocumentStatus.Draft,
      DocumentStatus.For_Review,
      DocumentStatus.For_Approval,
      DocumentStatus.Approved,
      DocumentStatus.Effective,
      DocumentStatus.For_Periodic_Review,
      DocumentStatus.Approval_Declined,
    ],
    statuses,
  );
};

export const ChangeOwnerButton = (props: ChangeOwnerButtonProps) => {
  const { t } = useTranslation();
  const { selectedDocuments } = props;
  const { data: tags = [] } = useTags();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const handleChangeOwner = () => setIsModalOpen(true);
  const { showToast } = useToastProvider();
  const { invalidate } = useTableDataInvalidation();

  const {
    isError: errorFetchingUsers,
    isLoading,
    data: users = [],
  } = useUsers();

  if (errorFetchingUsers) {
    showToast({
      'data-cy': 'bulk-v2-change-owner-users-error-toast',
      status: 'error',
      id: 'change-owner-users-error',
      title: t('notifications.error'),
      description: t('errors.errorLoadingApprovers').replace(
        'approvers',
        'users',
      ),
      replace: true,
    });
  }

  const onSuccess = (result: PromiseSettledResult<void>[]) => {
    const successful = result.filter(
      (promise) => promise.status === 'fulfilled',
    );
    const failures = result.filter((promise) => promise.status === 'rejected');

    if (successful.length) {
      showToast({
        'data-cy': 'bulk-v2-change-owner-success-toast',
        status: 'success',
        id: 'change-owner-success',
        title: t('notifications.ownerChanged'),
        description: t('success.successfullyChangedDocumentOwner'),
        replace: true,
      });
      void invalidate(props.currentTab);
    }

    if (failures.length) {
      const multipleFailures = t(
        'additionalMessages.failedToChangeOwnerForDocuments',
        { count: failures.length },
      );
      const singleFailure =
        (failures[0] as PromiseRejectedResult).reason?.response?.data
          ?.message || multipleFailures;
      const message = failures.length > 1 ? multipleFailures : singleFailure;
      showToast({
        'data-cy': 'bulk-v2-change-owner-error-toast',
        status: 'error',
        id: 'change-owner-error',
        title: t('notifications.error'),
        description: message,
        replace: true,
      });
    }
    setIsModalOpen(false);
  };

  const handleConfirmChangeOwner = async (
    selectedNewOwnerId: number,
  ): Promise<PromiseSettledResult<QualioDocument>[]> => {
    return Promise.allSettled(
      selectedDocuments.map((doc) => {
        return documentApi.update({ owner_id: selectedNewOwnerId }, doc.id);
      }),
    );
  };

  const changeOwnerText = t('modals.changeOwnerForDocument');

  const documentIdToRequiredGroupIdsMap = useMemo(() => {
    return selectedDocuments?.reduce((acc, qualioDocument) => {
      const tagIdsToGroupIds = getDocumentTagIdsToGroupIdsMap(
        qualioDocument,
        tags,
      );
      return acc.set(qualioDocument.id, tagIdsToGroupIds);
    }, new Map<QualioDocument['id'], Map<number, number[]>>());
  }, [selectedDocuments, tags]);

  const potentialOwners = useMemo(
    () =>
      users.filter((user) => {
        return selectedDocuments.every((doc) => {
          const requiredGroupIds = documentIdToRequiredGroupIdsMap?.get(doc.id);
          if (requiredGroupIds?.size) {
            return canUserAccessAtLeastOnePrivateTag(
              user,
              Array.from(requiredGroupIds.values()),
            );
          }
          return true;
        });
      }),
    [documentIdToRequiredGroupIdsMap, selectedDocuments, users],
  );

  return (
    <>
      <BulkActionButton
        isActionAllowed={canChangeOwner}
        {...props}
        onClick={handleChangeOwner}
      />
      <ChangeDocumentOwnerModal
        isModalOpen={isModalOpen}
        isLoading={isLoading}
        modalDescription={changeOwnerText}
        users={potentialOwners}
        onClose={() => setIsModalOpen(false)}
        handleChangeOwner={handleConfirmChangeOwner}
        onSuccess={onSuccess}
      />
    </>
  );
};
